import {
  AuthApiError,
  Session,
  VerifyEmailOtpParams,
} from '@supabase/supabase-js'
import { useQueryClient } from '@tanstack/react-query'
import { useRouter } from 'next/router'
import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'

import {
  createComponentClient,
  removeSupabaseCookieAndToken,
} from '@/plugins/supabase'
import { SupabaseUserMetadata } from '@/models'
import { apiClient } from '@/services/api/client'
import {
  APP_LOCAL_STORAGE_KEY_PREFIX,
  DEFAULT_AUTHENTICATED_PAGE,
} from '@/constants'
import { getAuthConfirmationUrl, isPrivateRoute } from '@/utils'

import { AuthContext } from './authContext'

const REFRESHING_TIME_GAP_BEFORE_EXPIRE_IN_SECS = 10

const returnToKey = `${APP_LOCAL_STORAGE_KEY_PREFIX}returnTo`

export function AuthContextProvider({ children }: PropsWithChildren) {
  const router = useRouter()

  const supabase = createComponentClient()

  const [session, setSession] = useState<Session | null>()

  const [isLoading, setIsLoading] = useState(false)

  const [passSetupTokenHash, setPassSetupTokenHash] = useState<string | null>(
    null,
  )

  const queryClient = useQueryClient()

  const userMetadata = useMemo(
    () => (session?.user?.user_metadata || {}) as SupabaseUserMetadata,
    [session],
  )

  const jwt = useMemo(() => session?.access_token, [session?.access_token])

  const updateToken = useCallback(
    async (
      newAccessToken: string,
      onSuccess?: () => void,
      newRefreshToken?: string,
    ) => {
      const refreshToken = newRefreshToken || session?.refresh_token
      if (!refreshToken) return

      const { error } = await supabase.auth.setSession({
        access_token: newAccessToken,
        refresh_token: refreshToken,
      })

      if (error) {
        console.error('Failed to update token', error)
        return
      }

      onSuccess?.()
    },
    [session, supabase],
  )

  const getSessionAsync = useCallback(async () => {
    setIsLoading(true)

    const {
      data: { session },
      error,
    } = await supabase.auth.getSession()

    if (error) {
      console.error('Error fetching session:', error)
      setSession(null)
      return
    }

    setSession(session)
    setIsLoading(false)
  }, [supabase])

  const refreshSession = useCallback(async () => {
    setIsLoading(true)
    const { error } = await supabase.auth.refreshSession()
    if (error) {
      console.error('Failed to refresh session - Signing out', error)
      supabase.auth.signOut()
      setIsLoading(false)
      return
    }

    setIsLoading(false)
  }, [supabase])

  const scheduleTokenRefresh = useCallback(
    (rawSession: Session) => {
      const expirationTime =
        rawSession.expires_in - REFRESHING_TIME_GAP_BEFORE_EXPIRE_IN_SECS
      const refreshTimeInMs = expirationTime * 1000

      const scheduleTimeout = setTimeout(async () => {
        const { error } = await supabase.auth.refreshSession()

        if (error) {
          console.error('Failed to refresh session - Signing out', error)
          supabase.auth.signOut()
        }
      }, refreshTimeInMs)

      return scheduleTimeout
    },
    [supabase],
  )

  const goToInitialPage = useCallback(() => {
    const returnTo = localStorage.getItem(returnToKey)

    if (returnTo) {
      router.push(returnTo).then(() => {
        localStorage.removeItem(returnToKey)
      })
      return
    }

    const pathname = router.pathname

    if (
      !isPrivateRoute(pathname) ||
      pathname === '/login' ||
      pathname === '/'
    ) {
      router.push(`/${DEFAULT_AUTHENTICATED_PAGE}`)
    }
  }, [router])

  const getCurrentToken = useCallback(() => session?.access_token, [session])

  const signIn = useCallback(
    async (email: string, password: string) => {
      setIsLoading(true)
      const response = await supabase.auth.signInWithPassword({
        email,
        password,
      })

      setIsLoading(false)

      return response
    },
    [supabase],
  )

  const signInWithGoogle = useCallback(async () => {
    setIsLoading(true)
    const response = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: getAuthConfirmationUrl(),
        queryParams: {
          access_type: 'offline',
          prompt: 'consent',
        },
      },
    })
    setIsLoading(false)

    return response
  }, [supabase])

  const signUp = useCallback(
    async (email: string, password: string) => {
      setIsLoading(true)
      const response = await supabase.auth.signUp({
        email,
        password,
      })

      setIsLoading(false)

      return response
    },
    [supabase],
  )

  const updatePassword = useCallback(
    async (password: string) => {
      setIsLoading(true)
      const response = await supabase.auth.updateUser({ password })
      setIsLoading(false)
      return response
    },
    [supabase],
  )

  const resetPassword = useCallback(
    async (email: string) => {
      setIsLoading(true)
      const response = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: getAuthConfirmationUrl(),
      })
      setIsLoading(false)
      return response
    },
    [supabase],
  )

  const verifyEmailOtp = useCallback(
    async (params: Pick<VerifyEmailOtpParams, 'email' | 'token'>) => {
      setIsLoading(true)
      const response = await supabase.auth.verifyOtp({
        ...params,
        type: 'email',
      })
      setIsLoading(false)
      return response
    },
    [supabase],
  )

  const signOut = useCallback(async () => {
    setIsLoading(true)
    const response = await supabase.auth.signOut()

    if (response.error) {
      const isAuthApiError = response.error instanceof AuthApiError
      if (isAuthApiError && response.error.status === 403) {
        removeSupabaseCookieAndToken()
      }
    }

    setSession(null)
    queryClient.clear()

    setIsLoading(false)

    router.push('/login')
  }, [supabase, router, queryClient])

  useEffect(() => {
    getSessionAsync()

    supabase.auth.stopAutoRefresh()

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session)
    })

    return () => {
      subscription.unsubscribe()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!session) return

    const scheduleStopper = scheduleTokenRefresh(session)

    return () => {
      !!scheduleStopper && clearTimeout(scheduleStopper)
    }
  }, [session, scheduleTokenRefresh])

  useEffect(() => {
    if (jwt) {
      apiClient.setAuthorizationHeader(jwt)
    }
  }, [jwt])

  return (
    <AuthContext.Provider
      value={{
        jwt,
        userMetadata,
        session,
        updateToken,
        isLoading,
        getCurrentToken,
        goToInitialPage,
        signOut,
        signIn,
        signUp,
        updatePassword,
        resetPassword,
        verifyEmailOtp,
        passSetupTokenHash,
        setPassSetupTokenHash,
        signInWithGoogle,
        refreshSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}
